import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    CircularProgress,
    Snackbar,
    Alert,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Chip,
    Divider,
    Tooltip,
    LinearProgress,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem,
    Grid,
    alpha,
    Skeleton,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    Category as CategoryIcon,
    Inventory as GroupIcon,
    SubdirectoryArrowRight as SubgroupIcon,
    InsertDriveFile as FileIcon,
    ExpandLess,
    ExpandMore,
    Refresh as RefreshIcon,
    CloudUpload as UploadIcon,
    Image as ImageIcon,
    MoreVert as MoreVertIcon,
    Description as DescriptionIcon,
    PictureAsPdf as PdfIcon,
    Article as ArticleIcon,
    Visibility as VisibilityIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    SwapVert as ReorderIcon,
    DragIndicator as DragIndicatorIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import adminResourcesService from '../services/adminResourcesService';
import { convertToSignedUrl } from '../utils/wasabiHelper';


// Helper to get signed URL for Wasabi images
const getImageUrl = (url) => {
    if (!url) return null;
    // Check if it's a Wasabi URL
    if (url.includes('wasabi') || url.includes('s3.ap-southeast-2')) {
        return convertToSignedUrl(url);
    }
    // Return as-is for external URLs
    return url;
};

// Helper to get file icon based on type
const getFileIcon = (fileType) => {
    const type = fileType?.toUpperCase();
    if (['PDF'].includes(type)) return <PdfIcon color="error" />;
    if (['DOC', 'DOCX'].includes(type)) return <ArticleIcon color="primary" />;
    if (['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(type)) return <ImageIcon color="success" />;
    return <DescriptionIcon color="action" />;
};

// Format file size
const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// Sortable Item Component for drag-drop reorder
function SortableItem({ item, index, theme }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none',
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.1) : 'action.hover',
                borderRadius: 1,
                mb: 0.5,
                border: '1px solid',
                borderColor: isDragging ? theme.palette.primary.main : 'divider',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
            }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <DragIndicatorIcon fontSize="small" color="action" />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Chip label={index + 1} size="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={item.name} />
        </ListItem>
    );
}

function ResourcesManagement() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Data state
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [subgroups, setSubgroups] = useState([]);
    const [selectedSubgroup, setSelectedSubgroup] = useState(null);
    const [files, setFiles] = useState([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loadingSubgroups, setLoadingSubgroups] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(''); // 'category', 'group', 'subgroup'
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [dialogData, setDialogData] = useState({ name: '', imageUrl: '', linkUrl: '' });
    const [editingItem, setEditingItem] = useState(null);
    const [groupImageFile, setGroupImageFile] = useState(null); // For direct image upload in group dialog

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // File upload
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadDisplayName, setUploadDisplayName] = useState('');

    // Thumbnail upload
    const [thumbnailDialogOpen, setThumbnailDialogOpen] = useState(false);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [thumbnailGroupId, setThumbnailGroupId] = useState(null);
    const [thumbnailCategoryId, setThumbnailCategoryId] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(''); // For URL input option

    // Snackbar
    const [mobileFilesDialogOpen, setMobileFilesDialogOpen] = useState(false); // For mobile files panel
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Context menu
    const [contextMenu, setContextMenu] = useState(null);
    const [contextTarget, setContextTarget] = useState(null);

    // Reorder state
    const [reorderMode, setReorderMode] = useState(null); // 'categories' | 'groups' | 'subgroups' | null
    const [reorderItems, setReorderItems] = useState([]);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 200, tolerance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Load categories on mount
    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const response = await adminResourcesService.getCategories();
            setCategories(response.data || []);
        } catch (error) {
            showSnackbar('Failed to load categories', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadGroups = async (categoryId) => {
        setLoadingGroups(true);
        try {
            const response = await adminResourcesService.getGroupsByCategory(categoryId);
            setGroups(response.data || []);
        } catch (error) {
            showSnackbar('Failed to load groups', 'error');
        } finally {
            setLoadingGroups(false);
        }
    };

    const loadSubgroups = async (groupId) => {
        setLoadingSubgroups(true);
        try {
            const response = await adminResourcesService.getSubgroupsByGroup(groupId);
            setSubgroups(response.data || []);
        } catch (error) {
            showSnackbar('Failed to load subgroups', 'error');
        } finally {
            setLoadingSubgroups(false);
        }
    };

    const loadFiles = async (subgroupId) => {
        setLoadingFiles(true);
        try {
            const response = await adminResourcesService.getFilesBySubgroup(subgroupId);
            setFiles(response.data || []);
        } catch (error) {
            showSnackbar('Failed to load files', 'error');
        } finally {
            setLoadingFiles(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    // Category handlers
    const handleCategoryClick = async (category) => {
        setSelectedCategory(category);
        setSelectedGroup(null);
        setSelectedSubgroup(null);
        setGroups([]);
        setSubgroups([]);
        setFiles([]);

        const isExpanded = expandedCategories[category.id];
        // Only one category can be expanded at a time - close all others
        setExpandedCategories({ [category.id]: !isExpanded });

        if (!isExpanded) {
            await loadGroups(category.id);
        }
    };

    // Group handlers  
    const handleGroupClick = async (group) => {
        setSelectedGroup(group);
        setSelectedSubgroup(null);
        setSubgroups([]);
        setFiles([]);

        const isExpanded = expandedGroups[group.id];
        // Only one group can be expanded at a time - close all others
        setExpandedGroups({ [group.id]: !isExpanded });

        if (!isExpanded) {
            await loadSubgroups(group.id);
        }

        // On desktop, clicking also shows the details panel (already visible)
        // On mobile, we only expand/collapse - use eye icon to view
    };

    // View handler for mobile eye icon
    const handleViewGroup = async (group, e) => {
        e?.stopPropagation();
        setSelectedGroup(group);
        setSelectedSubgroup(null);
        setSubgroups([]);
        setFiles([]);
        await loadSubgroups(group.id);
        setMobileFilesDialogOpen(true);
    };

    // Subgroup handlers
    const handleSubgroupClick = async (subgroup) => {
        setSelectedSubgroup(subgroup);
        await loadFiles(subgroup.id);
        // On desktop, clicking shows files in right panel
        // On mobile, use eye icon to view
    };

    // View handler for mobile eye icon - subgroup
    const handleViewSubgroup = async (subgroup, e) => {
        e?.stopPropagation();
        setSelectedSubgroup(subgroup);
        await loadFiles(subgroup.id);
        setMobileFilesDialogOpen(true);
    };

    // Dialog handlers
    const openCreateDialog = (type) => {
        setDialogType(type);
        setDialogMode('create');
        setDialogData({ name: '', imageUrl: '', linkUrl: '' });
        setEditingItem(null);
        setGroupImageFile(null);
        setDialogOpen(true);
    };

    const openEditDialog = (type, item) => {
        setDialogType(type);
        setDialogMode('edit');
        setDialogData({ name: item.name, imageUrl: item.imageUrl || '', linkUrl: item.linkUrl || '' });
        setEditingItem(item);
        setGroupImageFile(null);
        setDialogOpen(true);
    };

    const handleDialogSubmit = async () => {
        if (!dialogData.name.trim()) {
            showSnackbar('Name is required', 'error');
            return;
        }

        setLoading(true);
        try {
            if (dialogMode === 'create') {
                if (dialogType === 'category') {
                    // Create category first
                    const createResult = await adminResourcesService.createCategory({
                        name: dialogData.name,
                        imageUrl: dialogData.imageUrl || '',
                    });

                    // If image file selected, upload it
                    if (groupImageFile && createResult.data?.id) {
                        try {
                            await adminResourcesService.uploadCategoryThumbnail(createResult.data.id, groupImageFile);
                        } catch (uploadErr) {
                            console.error('Failed to upload category image:', uploadErr);
                        }
                    }
                    await loadCategories();
                } else if (dialogType === 'group' && selectedCategory) {
                    const createResult = await adminResourcesService.createGroup(selectedCategory.id, {
                        name: dialogData.name,
                        imageUrl: dialogData.imageUrl || '',
                        linkUrl: dialogData.linkUrl || '',
                    });

                    // If image file selected, upload it
                    if (groupImageFile && createResult.data?.id) {
                        try {
                            await adminResourcesService.uploadThumbnail(createResult.data.id, groupImageFile);
                        } catch (uploadErr) {
                            console.error('Failed to upload group image:', uploadErr);
                        }
                    }
                    await loadGroups(selectedCategory.id);
                } else if (dialogType === 'subgroup' && selectedGroup) {
                    await adminResourcesService.createSubgroup(selectedGroup.id, { name: dialogData.name });
                    await loadSubgroups(selectedGroup.id);
                }
                showSnackbar(`${dialogType} created successfully`);
            } else {
                if (dialogType === 'category') {
                    // Update category
                    await adminResourcesService.updateCategory(editingItem.id, {
                        name: dialogData.name,
                        imageUrl: dialogData.imageUrl,
                    });

                    // If new image file selected, upload it
                    if (groupImageFile) {
                        try {
                            await adminResourcesService.uploadCategoryThumbnail(editingItem.id, groupImageFile);
                        } catch (uploadErr) {
                            console.error('Failed to upload category image:', uploadErr);
                        }
                    }
                    await loadCategories();
                } else if (dialogType === 'group') {
                    // Update group
                    await adminResourcesService.updateGroup(editingItem.id, {
                        name: dialogData.name,
                        imageUrl: dialogData.imageUrl,
                        linkUrl: dialogData.linkUrl || '',
                    });

                    // If new image file selected, upload it
                    if (groupImageFile) {
                        try {
                            await adminResourcesService.uploadThumbnail(editingItem.id, groupImageFile);
                        } catch (uploadErr) {
                            console.error('Failed to upload group image:', uploadErr);
                        }
                    }
                    if (selectedCategory) await loadGroups(selectedCategory.id);
                } else if (dialogType === 'subgroup') {
                    await adminResourcesService.updateSubgroup(editingItem.id, { name: dialogData.name });
                    if (selectedGroup) await loadSubgroups(selectedGroup.id);
                }
                showSnackbar(`${dialogType} updated successfully`);
            }
            setDialogOpen(false);
            setGroupImageFile(null);
        } catch (error) {
            showSnackbar(error.response?.data?.message || `Failed to ${dialogMode} ${dialogType}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Delete handlers
    const openDeleteDialog = (type, item) => {
        setDeleteTarget({ type, item });
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        setLoading(true);
        try {
            const { type, item } = deleteTarget;
            if (type === 'category') {
                await adminResourcesService.deleteCategory(item.id);
                await loadCategories();
                if (selectedCategory?.id === item.id) {
                    setSelectedCategory(null);
                    setGroups([]);
                }
            } else if (type === 'group') {
                await adminResourcesService.deleteGroup(item.id);
                if (selectedCategory) await loadGroups(selectedCategory.id);
                if (selectedGroup?.id === item.id) {
                    setSelectedGroup(null);
                    setSubgroups([]);
                }
            } else if (type === 'subgroup') {
                await adminResourcesService.deleteSubgroup(item.id);
                if (selectedGroup) await loadSubgroups(selectedGroup.id);
                if (selectedSubgroup?.id === item.id) {
                    setSelectedSubgroup(null);
                    setFiles([]);
                }
            } else if (type === 'file') {
                await adminResourcesService.deleteFile(item.id);
                if (selectedSubgroup) await loadFiles(selectedSubgroup.id);
                // Also reload subgroups to update fileCount in sidebar
                if (selectedGroup) await loadSubgroups(selectedGroup.id);
            }
            showSnackbar(`${type} deleted successfully`);
            setDeleteDialogOpen(false);
        } catch (error) {
            showSnackbar(error.response?.data?.message || `Failed to delete ${deleteTarget.type}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // File upload handlers
    const handleFileUpload = async () => {
        if (!uploadFile || !selectedSubgroup) return;

        setLoading(true);
        try {
            await adminResourcesService.uploadFile(
                selectedSubgroup.id,
                uploadFile,
                uploadDisplayName || null,
                (progress) => setUploadProgress(progress)
            );
            await loadFiles(selectedSubgroup.id);
            // Also reload subgroups to update fileCount in sidebar
            if (selectedGroup) await loadSubgroups(selectedGroup.id);
            showSnackbar('File uploaded successfully');
            setUploadDialogOpen(false);
            setUploadFile(null);
            setUploadDisplayName('');
            setUploadProgress(0);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to upload file', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Thumbnail upload handlers
    const handleThumbnailUpload = async () => {
        // Support both groups and categories
        if (!thumbnailGroupId && !thumbnailCategoryId) return;

        // Either file or URL must be provided
        if (!thumbnailFile && !thumbnailUrl.trim()) {
            showSnackbar('Please select an image or enter an image URL', 'error');
            return;
        }

        setLoading(true);
        try {
            if (thumbnailCategoryId) {
                // Handle category thumbnail
                if (thumbnailFile) {
                    await adminResourcesService.uploadCategoryThumbnail(thumbnailCategoryId, thumbnailFile);
                } else {
                    await adminResourcesService.updateCategory(thumbnailCategoryId, { imageUrl: thumbnailUrl.trim() });
                }
                await loadCategories();
                // Refresh selectedCategory if it's the one we just updated
                if (selectedCategory?.id === thumbnailCategoryId) {
                    const updatedCategory = await adminResourcesService.getCategoryById(thumbnailCategoryId);
                    if (updatedCategory?.data) setSelectedCategory(updatedCategory.data);
                }
            } else if (thumbnailGroupId) {
                // Handle group thumbnail
                if (thumbnailFile) {
                    await adminResourcesService.uploadThumbnail(thumbnailGroupId, thumbnailFile);
                } else {
                    await adminResourcesService.updateGroup(thumbnailGroupId, { imageUrl: thumbnailUrl.trim() });
                }
                if (selectedCategory) await loadGroups(selectedCategory.id);
                // Refresh selectedGroup if it's the one we just updated
                if (selectedGroup?.id === thumbnailGroupId) {
                    const updatedGroup = await adminResourcesService.getGroupById(thumbnailGroupId);
                    setSelectedGroup(updatedGroup.data);
                }
            }
            showSnackbar('Thumbnail updated successfully');
            setThumbnailDialogOpen(false);
            setThumbnailFile(null);
            setThumbnailUrl('');
            setThumbnailGroupId(null);
            setThumbnailCategoryId(null);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to update thumbnail', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openThumbnailDialog = (groupId, categoryId = null) => {
        setThumbnailGroupId(groupId);
        setThumbnailCategoryId(categoryId);
        setThumbnailFile(null);
        setThumbnailUrl('');
        setThumbnailDialogOpen(true);
    };

    // Context menu handlers
    const handleContextMenu = (event, type, item) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
        setContextTarget({ type, item });
    };

    const handleContextMenuClose = () => {
        setContextMenu(null);
        setContextTarget(null);
    };

    // Reorder handlers
    const startReorder = (type) => {
        let items = [];
        if (type === 'categories') {
            items = [...categories];
        } else if (type === 'groups') {
            items = [...groups];
        } else if (type === 'subgroups') {
            items = [...subgroups];
        }
        setReorderMode(type);
        setReorderItems(items);
        handleContextMenuClose();
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setReorderItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const saveReorder = async () => {
        setLoading(true);
        try {
            const orderedIds = reorderItems.map(item => item.id);
            if (reorderMode === 'categories') {
                await adminResourcesService.reorderCategories(orderedIds);
                await loadCategories();
            } else if (reorderMode === 'groups' && selectedCategory) {
                await adminResourcesService.reorderGroups(selectedCategory.id, orderedIds);
                await loadGroups(selectedCategory.id);
            } else if (reorderMode === 'subgroups' && selectedGroup) {
                await adminResourcesService.reorderSubgroups(selectedGroup.id, orderedIds);
                await loadSubgroups(selectedGroup.id);
            }
            showSnackbar('Order saved successfully');
            setReorderMode(null);
            setReorderItems([]);
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to save order', 'error');
        } finally {
            setLoading(false);
        }
    };

    const cancelReorder = () => {
        setReorderMode(null);
        setReorderItems([]);
    };

    // Render tree navigation
    const renderTreeNavigation = () => (
        <Paper sx={{ height: '100%', overflow: 'auto' }}>
            {/* Reorder Mode Header */}
            {reorderMode ? (
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" color="primary">
                            Reorder {reorderMode === 'categories' ? 'Categories' : reorderMode === 'groups' ? 'Groups' : 'Subgroups'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" startIcon={<CancelIcon />} onClick={cancelReorder}>Cancel</Button>
                            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveReorder} disabled={loading}>
                                Save Order
                            </Button>
                        </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Drag items to reorder
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={reorderItems.map(item => item.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <List dense>
                                {reorderItems.map((item, index) => (
                                    <SortableItem
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        theme={theme}
                                    />
                                ))}
                            </List>
                        </SortableContext>
                    </DndContext>
                </Box>
            ) : (
                <>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Resources</Typography>
                        <Box>
                            <Tooltip title="Refresh">
                                <IconButton onClick={loadCategories} size="small">
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Add Category">
                                <IconButton onClick={() => openCreateDialog('category')} size="small" color="primary">
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Divider />
                    <List dense>
                        {/* Skeleton loading for initial categories */}
                        {loading && categories.length === 0 && (
                            <>
                                {[1, 2].map((i) => (
                                    <ListItem key={`skeleton-category-${i}`}>
                                        <ListItemIcon>
                                            <Skeleton animation="wave" variant="circular" width={24} height={24} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Skeleton animation="wave" variant="text" width="70%" />}
                                            secondary={<Skeleton animation="wave" variant="text" width="40%" />}
                                        />
                                    </ListItem>
                                ))}
                            </>
                        )}
                        {categories.map((category) => (
                            <React.Fragment key={category.id}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {isMobile && (
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedCategory(category);
                                                        setSelectedGroup(null);
                                                        setMobileFilesDialogOpen(true);
                                                    }}>
                                                        <VisibilityIcon fontSize="small" color="success" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {isMobile && (
                                                <IconButton size="small" onClick={() => handleCategoryClick(category)}>
                                                    {expandedCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            )}
                                            <IconButton size="small" onClick={(e) => handleContextMenu(e, 'category', category)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    }
                                >
                                    <ListItemButton
                                        onClick={() => handleCategoryClick(category)}
                                        selected={selectedCategory?.id === category.id}
                                        sx={{ pr: isMobile ? 12 : 6 }}
                                    >
                                        <ListItemIcon>
                                            {expandedCategories[category.id] ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={category.name}
                                            secondary={`${category.groupCount || 0} groups`}
                                        />
                                        {/* Chevron on desktop only */}
                                        {!isMobile && (expandedCategories[category.id] ? <ExpandLess /> : <ExpandMore />)}
                                    </ListItemButton>
                                </ListItem>

                                {/* Groups collapse */}
                                <Collapse in={expandedCategories[category.id]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding dense>
                                        {selectedCategory?.id === category.id && (
                                            <>
                                                {loadingGroups && (
                                                    <ListItem sx={{ pl: 4 }}>
                                                        <ListItemIcon>
                                                            <Skeleton animation="wave" variant="circular" width={24} height={24} />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={<Skeleton animation="wave" variant="text" width="60%" />}
                                                            secondary={<Skeleton animation="wave" variant="text" width="30%" />}
                                                        />
                                                    </ListItem>
                                                )}
                                                {!loadingGroups && (
                                                    <ListItem sx={{ pl: 4 }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            onClick={() => openCreateDialog('group')}
                                                        >
                                                            Add Group
                                                        </Button>
                                                    </ListItem>
                                                )}
                                                {!loadingGroups && groups.map((group) => (
                                                    <React.Fragment key={group.id}>
                                                        <ListItem
                                                            disablePadding
                                                            sx={{ pl: isMobile ? 1 : 4 }}
                                                            secondaryAction={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', }}>
                                                                    {/* Chevron on mobile - moved here to avoid overlap */}
                                                                    {isMobile && (
                                                                        <Tooltip title="View Details">
                                                                            <IconButton size="small" onClick={(e) => handleViewGroup(group, e)}>
                                                                                <VisibilityIcon fontSize="small" color="success" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {isMobile && (
                                                                        <IconButton size="small" onClick={() => handleGroupClick(group)}>
                                                                            {expandedGroups[group.id] ? <ExpandLess /> : <ExpandMore />}
                                                                        </IconButton>
                                                                    )}
                                                                    <IconButton size="small" onClick={(e) => handleContextMenu(e, 'group', group)}>
                                                                        <MoreVertIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            }
                                                        >
                                                            <ListItemButton
                                                                onClick={() => handleGroupClick(group)}
                                                                selected={selectedGroup?.id === group.id}
                                                                sx={{ pr: isMobile ? 12 : 6 }}
                                                            >
                                                                <ListItemIcon>
                                                                    <GroupIcon color="secondary" />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={group.name}
                                                                    secondary={`${group.subgroupCount || 0} subgroups`}
                                                                    primaryTypographyProps={{ fontSize: 13 }}
                                                                    secondaryTypographyProps={{ fontSize: 12 }}
                                                                />
                                                                {/* Chevron on desktop only */}
                                                                {!isMobile && (expandedGroups[group.id] ? <ExpandLess /> : <ExpandMore />)}
                                                            </ListItemButton>
                                                        </ListItem>

                                                        {/* Subgroups collapse */}
                                                        <Collapse in={expandedGroups[group.id]} timeout="auto" unmountOnExit>
                                                            <List component="div" disablePadding dense>
                                                                {selectedGroup?.id === group.id && (
                                                                    <>
                                                                        {loadingSubgroups ? (
                                                                            <ListItem sx={{ pl: 8 }}>
                                                                                <ListItemIcon>
                                                                                    <Skeleton animation="wave" variant="circular" width={20} height={20} />
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    primary={<Skeleton animation="wave" variant="text" width="50%" />}
                                                                                    secondary={<Skeleton animation="wave" variant="text" width="25%" />}
                                                                                />
                                                                            </ListItem>
                                                                        ) : (
                                                                            <>
                                                                                <ListItem sx={{ pl: 8 }}>
                                                                                    <Button
                                                                                        size="small"
                                                                                        startIcon={<AddIcon />}
                                                                                        onClick={() => openCreateDialog('subgroup')}
                                                                                    >
                                                                                        Add Subgroup
                                                                                    </Button>
                                                                                </ListItem>
                                                                            </>
                                                                        )}
                                                                        {!loadingSubgroups && subgroups.map((subgroup) => (
                                                                            <ListItem
                                                                                key={subgroup.id}
                                                                                disablePadding
                                                                                sx={{ pl: 8 }}
                                                                                secondaryAction={
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                        {isMobile && (
                                                                                            <Tooltip title="View Files">
                                                                                                <IconButton size="small" onClick={(e) => handleViewSubgroup(subgroup, e)}>
                                                                                                    <VisibilityIcon fontSize="small" color="success" />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        )}
                                                                                        <IconButton size="small" onClick={(e) => handleContextMenu(e, 'subgroup', subgroup)}>
                                                                                            <MoreVertIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                }
                                                                            >
                                                                                <ListItemButton
                                                                                    onClick={() => handleSubgroupClick(subgroup)}
                                                                                    selected={selectedSubgroup?.id === subgroup.id}
                                                                                    sx={{ pr: isMobile ? 14 : 4 }}
                                                                                >
                                                                                    <ListItemIcon>
                                                                                        <SubgroupIcon color="info" />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText
                                                                                        primary={subgroup.name}
                                                                                        secondary={`${subgroup.fileCount || 0} files`}
                                                                                    />
                                                                                </ListItemButton>
                                                                            </ListItem>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </List>
                                                        </Collapse>
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        )}
                                    </List>
                                </Collapse>
                            </React.Fragment>
                        ))
                        }
                    </List >
                </>
            )}
        </Paper >
    );

    // Render files panel
    const renderFilesPanel = () => (
        <Paper sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            {selectedSubgroup ? (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6">{selectedSubgroup.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCategory?.name} → {selectedGroup?.name}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadDialogOpen(true)}
                        >
                            Upload File
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    {loadingFiles ? (
                        <Grid container spacing={2}>
                            {[1, 2, 3].map((i) => (
                                <Grid item xs={12} sm={6} md={4} key={`skeleton-file-${i}`}>
                                    <Card>
                                        <CardContent sx={{ pb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Skeleton variant="circular" width={24} height={24} />
                                                <Skeleton variant="text" width="80%" />
                                            </Box>
                                            <Skeleton variant="text" width="40%" />
                                        </CardContent>
                                        <CardActions sx={{ pt: 0 }}>
                                            <Skeleton variant="rectangular" width={60} height={30} />
                                            <Skeleton variant="circular" width={30} height={30} sx={{ ml: 'auto' }} />
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : files.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <FileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                            <Typography color="text.secondary">No files in this subgroup</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {files.map((file) => (
                                <Grid item xs={12} sm={6} md={4} key={file.id}>
                                    <Card>
                                        <CardContent sx={{ pb: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getFileIcon(file.fileType)}
                                                <Typography variant="subtitle2" noWrap title={file.name}>
                                                    {file.name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {file.fileType} • {formatFileSize(file.fileSize)}
                                            </Typography>
                                        </CardContent>
                                        <CardActions sx={{ pt: 0 }}>
                                            <Button
                                                size="small"
                                                href={file.fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Download
                                            </Button>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => openDeleteDialog('file', file)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            ) : selectedGroup ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6">{selectedGroup.name}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Select a subgroup to view files
                    </Typography>
                    {selectedGroup.imageUrl ? (
                        <Box
                            component="img"
                            src={getImageUrl(selectedGroup.imageUrl)}
                            alt={selectedGroup.name}
                            sx={{ maxWidth: '100%', maxHeight: 400, borderRadius: 2, objectFit: 'contain' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    {selectedGroup.imageUrl && (
                        <Box
                            sx={{
                                display: 'none',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 3,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                maxWidth: 400,
                                mx: 'auto'
                            }}
                        >
                            <ImageIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Image failed to load
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            onClick={() => openThumbnailDialog(selectedGroup.id)}
                        >
                            {selectedGroup.imageUrl ? 'Change Thumbnail' : 'Add Thumbnail'}
                        </Button>
                    </Box>
                </Box>
            ) : selectedCategory ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6">{selectedCategory.name}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Select a group to view details
                    </Typography>
                    {selectedCategory.imageUrl ? (
                        <Box
                            component="img"
                            src={getImageUrl(selectedCategory.imageUrl)}
                            alt={selectedCategory.name}
                            sx={{ maxWidth: '100%', maxHeight: 400, borderRadius: 2, objectFit: 'contain' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    {selectedCategory.imageUrl && (
                        <Box
                            sx={{
                                display: 'none',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 3,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                maxWidth: 400,
                                mx: 'auto'
                            }}
                        >
                            <ImageIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Image failed to load
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            onClick={() => openThumbnailDialog(null, selectedCategory.id)}
                        >
                            {selectedCategory.imageUrl ? 'Change Thumbnail' : 'Add Thumbnail'}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CategoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography color="text.secondary">
                        Select a category to get started
                    </Typography>
                </Box>
            )}
        </Paper>
    );

    return (
        <Box sx={{ height: 'calc(100vh - 100px)' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
                    >
                        Resources Management
                    </Typography>
                </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 1 }} />}

            {/* Main content */}
            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100% - 60px)', flexDirection: isMobile ? 'column' : 'row' }}>
                {/* Tree navigation - left side */}
                <Box sx={{
                    width: isMobile ? '100%' : 280,
                    flexShrink: 0,
                    minWidth: isMobile ? 'auto' : 280,
                    height: isMobile ? 'auto' : '100%',
                    overflow: isMobile ? 'visible' : 'auto'
                }}>
                    {renderTreeNavigation()}
                </Box>

                {/* Files panel - right side (desktop only) */}
                {!isMobile && (
                    <Box sx={{ flex: 1 }}>
                        {renderFilesPanel()}
                    </Box>
                )}
            </Box>

            {/* Mobile Files Panel Dialog */}
            {isMobile && (
                <Dialog
                    open={mobileFilesDialogOpen}
                    onClose={() => setMobileFilesDialogOpen(false)}
                    fullScreen
                >
                    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {selectedSubgroup?.name || selectedGroup?.name || 'Files'}
                            </Typography>
                            <Button onClick={() => setMobileFilesDialogOpen(false)}>Close</Button>
                        </Box>
                        {renderFilesPanel()}
                    </Box>
                </Dialog>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' ? `Create ${dialogType}` : `Edit ${dialogType}`}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name"
                        fullWidth
                        variant="outlined"
                        value={dialogData.name}
                        onChange={(e) => setDialogData({ ...dialogData, name: e.target.value })}
                    />
                    {(dialogType === 'category' || dialogType === 'group') && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {dialogType === 'category' ? 'Category' : 'Group'} Image (optional)
                            </Typography>

                            {/* Image Upload Button */}
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<ImageIcon />}
                                sx={{ mb: 1 }}
                            >
                                {groupImageFile ? groupImageFile.name : 'Upload Image'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                        setGroupImageFile(e.target.files[0]);
                                        setDialogData({ ...dialogData, imageUrl: '' }); // Clear URL if uploading file
                                    }}
                                />
                            </Button>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1 }}>
                                — OR —
                            </Typography>

                            {/* Image URL Input */}
                            <TextField
                                margin="dense"
                                label="Image URL"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={dialogData.imageUrl}
                                onChange={(e) => {
                                    setDialogData({ ...dialogData, imageUrl: e.target.value });
                                    setGroupImageFile(null); // Clear file if entering URL
                                }}
                                disabled={!!groupImageFile}
                            />

                            {/* Image Preview */}
                            {(groupImageFile || dialogData.imageUrl) && (
                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ textAlign: 'center', position: 'relative' }}>
                                        <img
                                            src={groupImageFile ? URL.createObjectURL(groupImageFile) : getImageUrl(dialogData.imageUrl)}
                                            alt="Preview"
                                            style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 8, objectFit: 'cover' }}
                                            onError={(e) => {
                                                e.target.src = '';
                                                e.target.alt = 'Failed to load';
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => {
                                                setGroupImageFile(null);
                                                setDialogData({ ...dialogData, imageUrl: '' });
                                            }}
                                        >
                                            Remove Image
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Link URL Input - Only for groups */}
                    {dialogType === 'group' && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                margin="dense"
                                label="External Link URL (optional)"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={dialogData.linkUrl}
                                onChange={(e) => setDialogData({ ...dialogData, linkUrl: e.target.value })}
                                placeholder="https://example.com/calculator"
                                helperText="If set, clicking this group will open this link instead of showing subgroups"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDialogSubmit} variant="contained" disabled={loading}>
                        {dialogMode === 'create' ? 'Create' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this {deleteTarget?.type}?
                        {deleteTarget?.type !== 'file' && (
                            <strong> All children and files will also be deleted from storage.</strong>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* File Upload Dialog */}
            <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload File</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            margin="dense"
                            label="Display Name (optional)"
                            fullWidth
                            variant="outlined"
                            value={uploadDisplayName}
                            onChange={(e) => setUploadDisplayName(e.target.value)}
                            helperText="Leave empty to use the original filename"
                        />
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ mt: 2, py: 2 }}
                            startIcon={<UploadIcon />}
                        >
                            {uploadFile ? uploadFile.name : 'Select File'}
                            <input
                                type="file"
                                hidden
                                onChange={(e) => setUploadFile(e.target.files[0])}
                            />
                        </Button>
                        {uploadProgress > 0 && (
                            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2 }} />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleFileUpload}
                        variant="contained"
                        disabled={!uploadFile || loading}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Thumbnail Upload Dialog */}
            <Dialog open={thumbnailDialogOpen} onClose={() => setThumbnailDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Group Thumbnail</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        {/* Upload Image Button */}
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ py: 2 }}
                            startIcon={<UploadIcon />}
                        >
                            {thumbnailFile ? thumbnailFile.name : 'Upload Image'}
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                    setThumbnailFile(e.target.files[0]);
                                    setThumbnailUrl(''); // Clear URL if selecting file
                                }}
                            />
                        </Button>

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', my: 1.5 }}>
                            — OR —
                        </Typography>

                        {/* Image URL Input */}
                        <TextField
                            margin="dense"
                            label="Image URL"
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="https://example.com/image.jpg"
                            value={thumbnailUrl}
                            onChange={(e) => {
                                setThumbnailUrl(e.target.value);
                                setThumbnailFile(null); // Clear file if entering URL
                            }}
                            disabled={!!thumbnailFile}
                        />

                        {/* Preview */}
                        {(thumbnailFile || thumbnailUrl) && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <img
                                    src={thumbnailFile ? URL.createObjectURL(thumbnailFile) : thumbnailUrl}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setThumbnailDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleThumbnailUpload}
                        variant="contained"
                        disabled={(!thumbnailFile && !thumbnailUrl.trim()) || loading}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Context Menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                sx={{
                    '& .MuiPaper-root': {
                        minWidth: 150,
                        ...(isMobile && {
                            maxWidth: '90vw',
                        })
                    }
                }}
            >
                <MenuItem onClick={() => {
                    openEditDialog(contextTarget?.type, contextTarget?.item);
                    handleContextMenuClose();
                }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    Edit
                </MenuItem>
                <MenuItem onClick={() => {
                    if (contextTarget?.type) {
                        // Map type to plural: 'category' -> 'categories', 'group' -> 'groups', 'subgroup' -> 'subgroups'
                        const typeMap = { 'category': 'categories', 'group': 'groups', 'subgroup': 'subgroups' };
                        startReorder(typeMap[contextTarget.type]);
                    }
                }}>
                    <ListItemIcon><ReorderIcon fontSize="small" color="primary" /></ListItemIcon>
                    Reorder {contextTarget?.type === 'category' ? 'Categories' : contextTarget?.type === 'group' ? 'Groups' : 'Subgroups'}
                </MenuItem>
                <MenuItem onClick={() => {
                    openDeleteDialog(contextTarget?.type, contextTarget?.item);
                    handleContextMenuClose();
                }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <Typography color="error">Delete</Typography>
                </MenuItem>
            </Menu>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ResourcesManagement;
